import { ref, shallowRef } from 'vue';
import { Input, WebMidi, type NoteMessageEvent, type PortEvent } from 'webmidi';
import { CapacitorMuseTrainerMidi } from 'capacitor-musetrainer-midi';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { useStorage } from '@vueuse/core';
import { useToast } from '@/composables/useToast';
import { useMidiNoteMapping } from '@/composables/useMidiNoteMapping';
import { withFallbackTimeout } from '@/utils';
interface MidiMessage {
  type: 'noteOn' | 'noteOff',
  dataByte1: number,
  dataByte2: number
}
type MidiDevicesResult = { devices: string[] };

const isEnabled = ref(false);
const inputs = ref<{id: string; name: string;}[]>([]);
// shallowRef: Input is an EventEmitter holding 16 InputChannel objects. A deep
// ref would proxy that graph and hand a proxied `this` to addListener/
// removeListener. Reassigned wholesale; consumers only read `.id`.
const currentInput = shallowRef<Input>();
const notePlayed = ref({
  time: 0,
  note: 0
});
const isCapacitorMidiPluginAvailable = ref<boolean>();
// shallowRef: opaque handles — only ever reassigned, only `.remove()` is called.
const capacitorMidiDeviceChangeListener = shallowRef<PluginListenerHandle>();
const capacitorMidiCommandReceiveListener = shallowRef<PluginListenerHandle>();
// WebMidi is a global singleton; guards against enableMidi() being invoked more
// than once per session (e.g. the user retrying after an error) stacking up
// duplicate 'connected'/'disconnected' listeners that each redundantly refresh
// the input list on every device change.
let webMidiGlobalListenersAttached = false;
const selectedInputId = useStorage<string | undefined>('midi-input-id', undefined);
const { createToast } = useToast();
const CAPACITOR_MIDI_PLUGIN_NAME = 'CapacitorMuseTrainerMidi';
const MIDI_LIST_DEVICES_TIMEOUT_MS = 3000;

export function useMidi() {
  const { getDrumLabel } = useMidiNoteMapping();

  const getIsCapacitorMidiPluginAvailable = () => {    
    if (isCapacitorMidiPluginAvailable.value != null) {
      return isCapacitorMidiPluginAvailable.value;
    }

    const isAvailable = Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === "ios"
      && Capacitor.isPluginAvailable(CAPACITOR_MIDI_PLUGIN_NAME);

    isCapacitorMidiPluginAvailable.value = isAvailable;
    return isAvailable;
  };

  const shouldUseCapacitorMidi = () => {
    return Capacitor.getPlatform() === "ios" && getIsCapacitorMidiPluginAvailable();
  };

  const enableMidi = async () => {
    // If on iOS and Capacitor is available, use the MuseTrainer plugin
    if (shouldUseCapacitorMidi()) {
      try {

        if (capacitorMidiDeviceChangeListener.value) await capacitorMidiDeviceChangeListener.value.remove();
        if (capacitorMidiCommandReceiveListener.value) await capacitorMidiCommandReceiveListener.value.remove();
        
        // Set up event listeners for MIDI events
        capacitorMidiDeviceChangeListener.value = await CapacitorMuseTrainerMidi.addListener('deviceChange', (data: MidiDevicesResult) => {
          // console.log('MIDI device change:', data.devices);
          updateCapacitorMidiInputsList(data);
        });
        
        capacitorMidiCommandReceiveListener.value = await CapacitorMuseTrainerMidi.addListener('commandReceive', (message: MidiMessage) => {
          // console.log('MIDI message received:', message);
          
          // Check if this is a note on message
          if (message.type === 'noteOn') {
            // console.log(`MIDI note received: ${message.dataByte1} with velocity ${message.dataByte2}`);
            notePlayed.value = {
              time: performance.now(),
              note: message.dataByte1,
            };
          }
        });
        
        isEnabled.value = true;
        await updateInputsList();
        return true;
      } catch (err) {
        console.error('MuseTrainer MIDI Device could not be enabled:', err);
        return false;
      }
    } else {
      // If not on iOS or MuseTrainer plugin failed, use standard WebMidi API
      try {
        console.log("Enabling standard WebMidi API");
        await WebMidi.enable();
        isEnabled.value = true;
        await updateInputsList();

        if (!webMidiGlobalListenersAttached) {
          webMidiGlobalListenersAttached = true;
          WebMidi.addListener('connected', (e: PortEvent) => {
            if (e.port.type === 'input') {
              updateInputsList();
            } else {
                console.error("expected connected event", e);
            }
          });

          WebMidi.addListener('disconnected', (e: PortEvent) => {
            if (e.port.type === 'input') {
              updateInputsList();
              if (currentInput.value && e.port.id === currentInput.value.id) {
                currentInput.value = undefined;
                selectedInputId.value = undefined;
              }
            } else {
                console.error("expected disconnected event", e);
            }
          });
        }

        console.log('WebMidi enabled!');
        return true;
      } catch (err) {
        console.error('WebMidi could not be enabled:', err);
        createToast({
          type: 'error',
          title: 'MIDI Error',
          message: 'Could not enable MIDI access. Please check browser permissions and try again.'
        });
        return false;
      }
    }
  };

  const applyPreferredInputSelection = () => {
    if (!inputs.value.length) {
      currentInput.value = undefined;
      selectedInputId.value = undefined;
      return;
    }

    const preferredId = selectedInputId.value;
    if (preferredId && inputs.value.some((input) => input.id === preferredId)) {
      selectInput(preferredId);
      return;
    }

    // Auto-select first available input when nothing valid is selected.
    selectInput(inputs.value[0].id);
  };

  function updateCapacitorMidiInputsList(result: MidiDevicesResult) {
    if (result && result.devices) {
      inputs.value = result.devices.map((deviceName: string, index: number) => ({
        id: String(index),
        name: deviceName
      }));
      applyPreferredInputSelection();
    }
  }
  

  const updateInputsList = async () => {
    // If on iOS and Capacitor is available, use the MuseTrainer plugin
    if (shouldUseCapacitorMidi()) {    
      try {
        // Get iOS MIDI inputs through MuseTrainer plugin
        const result = await withFallbackTimeout<MidiDevicesResult>(
          CapacitorMuseTrainerMidi.listDevices(),
          () => ({ devices: [] }),
          MIDI_LIST_DEVICES_TIMEOUT_MS,
          'CapacitorMuseTrainerMidi.listDevices'
        );
        updateCapacitorMidiInputsList(result);
      } catch (err) {
        console.error('Error getting iOS MIDI devices:', err);
        inputs.value = [];
      }
    } else if (WebMidi.enabled) {
      // Standard WebMidi API
      inputs.value = WebMidi.inputs.map(input => ({
        id: input.id,
        name: input.name
      }));
      applyPreferredInputSelection();
    }
  };

  const selectInput = (inputId: string) => {
    if (!isEnabled.value) return false;
    
    // If on iOS and Capacitor is available, use the MuseTrainer plugin
    if (shouldUseCapacitorMidi()) {      
      try {
        // For iOS/MuseTrainer, we need to explicitly open the device
        const deviceNumber = parseInt(inputId, 10);
        
        if (!isNaN(deviceNumber)) {
          // Find the device in our inputs list and set it as current
          const selectedInput = inputs.value.find(input => input.id === inputId);
          if (selectedInput) {
            // Create a lightweight representation of the input device
            // that matches the WebMidi Input interface for API compatibility
            currentInput.value = {
              id: selectedInput.id,
              name: selectedInput.name,
            } as unknown as Input;
            selectedInputId.value = inputId;
            
            return true;
          }
        }
      } catch (err) {
        console.error('Error selecting iOS MIDI device:', err);
        return false;
      }
    } else if (WebMidi.enabled) {
      // Remove listeners from previous input if any
      if (currentInput.value) {
        currentInput.value.removeListener();
      }
      
      const selectedInput = WebMidi.getInputById(inputId);
      if (selectedInput) {
        currentInput.value = selectedInput;
        selectedInputId.value = inputId;
        
        // Add note listeners for game input
        currentInput.value.addListener('noteon', (e: NoteMessageEvent) => {
          // console.log(`MIDI note received: ${e.note.number} (${e.note.name}${e.note.octave}) (${getDrumLabel(e.note.number)})`);
          notePlayed.value = {
            time: performance.now(),
            note: e.note.number,
          }
        });
        
        return true;
      }
    }
    
    return false;
  };

  return {
    isEnabled,
    inputs,
    currentInput,
    notePlayed,
    enableMidi,
    selectInput,
    updateInputsList
  };
}
